import { ArrowLeft, Edit, Eye, Users, ChevronDown, Home, Check, Cloud, ChevronLeft, ChevronRight, Star, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/app/_module/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/app/_module/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/app/_module/components/ui/select";
import axiosInstance from "@/app/_module/services/axiosConfig";
import { formatPhoneNumber } from "@/app/_module/utils/formatters";
import { useUserPhoneNumbers } from "@/app/_module/hooks/usePhoneNumberPricing";
import { previewSmsFromNumber } from "@/app/(authenticated)/communication/_modules/services/sendSms";
import React, { useState, useEffect } from "react";
import { useRouter } from "nextjs-toploader/app";

import { type Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import { type GetLeadByIdResponseData } from "@/app/(authenticated)/leads/_modules/types/get-leads-by-id-type";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";

import getStatusColor from "../constants/get-status-color";
import { useAvailableStatuses, usePipelineStages } from "../hooks/queries";
import CreateLeadPolicy from "./CreateLeadPolicy";

// Display name mapping for statuses (matches LeadsTable)
const getStatusDisplayName = (status: string): string => {
  const displayNames: Record<string, string> = {
    'New lead': 'Leads',
    'new lead': 'Leads',
  };
  return displayNames[status] || status;
};
import DNCConfirmationModal from "./DNCConfirmationModal";
import { useGetHousehold } from "../hooks/households";
import { UpdatePolicyDialog } from "./UpdateLeadPolicy";
import ViewerDisabled from "@/app/_module/components/ViewerDisabled";
import type { Policy as UpdatePolicyType } from "../types/lead-with-policies-type";
import { getMostRecentPolicy } from "../utils/get-most-recent-policy";

interface LeadDetailsHeaderProps {
  lead: Lead | GetLeadByIdResponseData;
  isEditing: boolean;
  // View-only: lead shared with the user (e.g. @mention) but owned by another
  // agent. Hide the Edit button so it's clearly not theirs to edit.
  viewOnly?: boolean;
  isUpdating?: boolean;
  isAutoSaving?: boolean;
  lastSavedAt?: Date | null;
  onBack: () => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onStatusChange?: (leadId: string, newStatus: string) => Promise<void>;
  hasPolicies?: boolean;
  policyCount?: number;
  policies?: any[];
  onPolicyUpdate?: () => void;
  // Lead navigation
  onNextLead?: () => void;
  onPrevLead?: () => void;
  hasNextLead?: boolean;
  hasPrevLead?: boolean;
  currentLeadIndex?: number;
  totalLeads?: number;
  isLoadingNavigation?: boolean;
}

// Helper function to convert Policy to UpdatePolicy type
const convertPolicyForEdit = (policy: any): UpdatePolicyType => {
  if (!policy) return null as any;

  return {
    id: policy.id,
    policyNumber: policy.policyNumber || "",
    productType: policy.productType || "",
    status: policy.status || "",
    policyStage: policy.policyStage || "underwriting",
    premium: policy.premium || 0,
    carrierName: policy.carrierName || "",
    annualPremium: policy.annualPremium || "",
    issuedAt: policy.issuedAt ? (policy.issuedAt instanceof Date ? `${policy.issuedAt.getFullYear()}-${String(policy.issuedAt.getMonth() + 1).padStart(2, '0')}-${String(policy.issuedAt.getDate()).padStart(2, '0')}` : policy.issuedAt) : null,
    expiryDate: policy.expiryDate ? (policy.expiryDate instanceof Date ? `${policy.expiryDate.getFullYear()}-${String(policy.expiryDate.getMonth() + 1).padStart(2, '0')}-${String(policy.expiryDate.getDate()).padStart(2, '0')}` : policy.expiryDate) : null,
  } as UpdatePolicyType;
};

export const LeadDetailsHeader = function ({
  lead,
  isEditing,
  viewOnly = false,
  isUpdating = false,
  isAutoSaving = false,
  lastSavedAt = null,
  onBack,
  onEdit,
  onSave,
  onCancel,
  onStatusChange,
  hasPolicies = false,
  policyCount = 0,
  policies = [],
  onPolicyUpdate,
  onNextLead,
  onPrevLead,
  hasNextLead = false,
  hasPrevLead = false,
  currentLeadIndex,
  totalLeads,
  isLoadingNavigation = false,
}: LeadDetailsHeaderProps) {
  const router = useRouter();
  const parentLead = 'data' in lead ? lead.data?.parentLead : lead.parentLead;
  const leadId = 'data' in lead ? lead.data?.id : lead.id;
  const leadName = 'data' in lead ? lead.data?.name : lead.name;
  const [sendingReviewInvite, setSendingReviewInvite] = useState(false);
  const [reviewInviteOpen, setReviewInviteOpen] = useState(false);
  const [lpConnectOpen, setLpConnectOpen] = useState(false);
  // Show "Request Review" only for agents who have actually connected
  // Licensedproducer — everyone else never sees the button (no dead-end 404s).
  const { data: lpStatus } = useQuery({
    queryKey: ["licensedproducer", "status"],
    queryFn: async () => {
      const r = await axiosInstance.get("/api/auth/licensedproducer-status");
      return r?.data?.data || r?.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const lpLinked = !!lpStatus?.linked;
  // A2P verified → we can text the invite from the agent's own number.
  const lpSmsReady = !!lpStatus?.sms_ready;
  // "Send via" choice: email | sms | both (options depend on what the lead has + A2P).
  const [riChannel, setRiChannel] = useState<'email' | 'sms' | 'both'>('both');
  // "Send from" — the agent's numbers (shared react-query cache with the SMS
  // widget) and the same default the SMS composer uses: the backend's preview
  // (conversation thread → recent call → smart match), A2P-linked first as fallback.
  const { data: userPhoneNumbersRaw } = useUserPhoneNumbers();
  const riNumbers: any[] = React.useMemo(() => {
    const list: any[] = Array.isArray(userPhoneNumbersRaw) ? userPhoneNumbersRaw : ((userPhoneNumbersRaw as any)?.phoneNumbers || []);
    return [...list].sort((a, b) => Number(b?.campaignLinkStatus === 'linked') - Number(a?.campaignLinkStatus === 'linked'));
  }, [userPhoneNumbersRaw]);
  const [riFrom, setRiFrom] = useState<string>('');
  const [leadIdCopied, setLeadIdCopied] = useState(false);
  const [riFromHint, setRiFromHint] = useState<string>('');

  const reviewInviteLead: any = 'data' in lead ? lead.data : lead;
  const riEmail = reviewInviteLead?.email || undefined;
  const riPhone = reviewInviteLead?.phone || reviewInviteLead?.phoneNumber || undefined;
  const riFirst = (reviewInviteLead?.name || '').trim().split(' ')[0] || 'there';

  useEffect(() => {
    if (!reviewInviteOpen || !riPhone || !lpSmsReady) return;
    let cancelled = false;
    const owns = (n?: string) => !!n && riNumbers.some((p) => p?.phoneNumber === n);
    const fallback = riNumbers[0]?.phoneNumber || '';
    previewSmsFromNumber(riPhone)
      .then((preview) => {
        if (cancelled) return;
        if (owns(preview?.fromNumber)) {
          setRiFrom(preview!.fromNumber);
          setRiFromHint(preview?.explanation || '');
        } else {
          setRiFrom((cur) => (owns(cur) ? cur : fallback));
          setRiFromHint('');
        }
      })
      .catch(() => { if (!cancelled) setRiFrom((cur) => (owns(cur) ? cur : fallback)); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewInviteOpen, riPhone, lpSmsReady, riNumbers.length]);

  const sendReviewInviteNow = async () => {
    setSendingReviewInvite(true);
    try {
      const channels = [
        riEmail && (riChannel === 'email' || riChannel === 'both') ? 'email' : null,
        riPhone && lpSmsReady && (riChannel === 'sms' || riChannel === 'both') ? 'sms' : null,
      ].filter(Boolean);
      const r = await axiosInstance.post('/api/auth/licensedproducer-review-invite', {
        client_first: riFirst, client_email: riEmail, client_phone: riPhone, channels,
        from: channels.includes('sms') && riFrom ? riFrom : undefined,
      });
      const resp = r?.data?.data || r?.data;
      const parts = [resp?.email ? 'email' : null, resp?.sms ? 'text (from your number)' : null].filter(Boolean);
      if (resp?.cooldown_minutes) { toast.info(resp.message || 'Already sent — the same link is still active.'); setReviewInviteOpen(false); return; }
      if (parts.length) toast.success(`Review invite ${resp?.reused ? 're-sent (same link)' : 'sent'} — ${parts.join(' + ')}.`);
      else toast.error(resp?.sms_note || 'Invite created but no channel delivered — check the client contact info.');
      if (parts.length && resp?.sms_note) toast.info(resp.sms_note);
      setReviewInviteOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Could not send the review invite.');
    } finally {
      setSendingReviewInvite(false);
    }
  };
  const currentStatus = ('data' in lead ? lead.data?.leadStat : lead.leadStat) || "New lead";
  const noSaleReason = 'data' in lead ? lead.data?.noSaleReason : lead.noSaleReason;
  
  // Extract household info
  const householdId = ('data' in lead ? lead.data?.householdId : lead.householdId) || null;
  const householdData = 'data' in lead ? lead.data?.household : lead.household;
  const isPrimaryHouseholdMember = ('data' in lead ? lead.data?.isPrimaryHouseholdMember : lead.isPrimaryHouseholdMember) || false;
  
  // Fetch household details if we have a householdId but no household data
  const shouldFetchHousehold = !!householdId && !householdData;
  const { data: fetchedHouseholdData } = useGetHousehold(
    householdId || '',
    shouldFetchHousehold
  );

  // Parse household members to find primary lead
  let primaryLeadInfo = householdData?.primaryLead;

  if (!primaryLeadInfo && fetchedHouseholdData?.data) {
    // Get all members and sort by createdAt (earliest = primary lead)
    const members = fetchedHouseholdData.data.map((m: any) => m.dataValues || m);
    const sortedMembers = [...members].sort((a, b) => 
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    
    // First member (earliest created) is the primary lead
    const primaryMember = sortedMembers[0];
    if (primaryMember) {
      primaryLeadInfo = {
        id: primaryMember.id,
        name: primaryMember.name
      };
    }
  }

  // Determine if this is a household member (not the primary lead)
  const isHouseholdMember = !!householdId && !!primaryLeadInfo && primaryLeadInfo.id !== leadId;

  // Use shared hook for statuses (cached for 15 minutes)
  const { data: statusesData, isLoading: loadingStatuses } = useAvailableStatuses();
  const { data: pipelineStages } = usePipelineStages();

  // Merge system statuses with custom pipeline stages
  const statuses = React.useMemo(() => {
    const systemStatuses = statusesData?.statuses || [
      { value: "New lead", label: "New lead" },
      { value: "Appt", label: "Appointments" },
      { value: "Underwriting", label: "Underwriting" },
      { value: "Sold & Approved", label: "Sold & Approved" },
      { value: "Bad number", label: "Bad number" },
      { value: "No sale", label: "No sale" },
      { value: "Do not call", label: "Do not call" },
      { value: "Declined", label: "Declined" },
      { value: "Lapsed", label: "Lapsed" },
      { value: "Disposed", label: "Disposed" },
    ];

    if (!pipelineStages || pipelineStages.length === 0) return systemStatuses;

    // Build from pipeline stages to include custom ones with colors
    const systemValues = new Set(systemStatuses.map((s: any) => s.value?.toLowerCase()));
    const merged = [...systemStatuses];

    for (const stage of pipelineStages) {
      const stageValue = stage.systemValue || stage.name;
      // Only add custom stages that aren't already in system statuses
      if (!systemValues.has(stageValue.toLowerCase()) && !systemValues.has(stage.name.toLowerCase())) {
        merged.push({ value: stageValue, label: stage.name, color: stage.color } as any);
      } else {
        // Add color to existing system status from pipeline stage
        const existing = merged.find((s: any) => s.value?.toLowerCase() === stageValue.toLowerCase() || s.value?.toLowerCase() === stage.name.toLowerCase());
        if (existing && stage.color) {
          (existing as any).color = stage.color;
        }
      }
    }

    return merged;
  }, [statusesData, pipelineStages]);

  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showDNCModal, setShowDNCModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<string | null>(null);
  const [showEditPolicyModal, setShowEditPolicyModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<UpdatePolicyType | null>(null);

  useEffect(() => {
  }, [leadId, leadName, hasPolicies, policyCount, policies]);

  const handleStatusChange = (newStatus: string) => {
    if (newStatus === currentStatus) return;

    const dncStatuses = ["Do not call", "DNC", "Do Not Call"];
    const isChangingToDNC = dncStatuses.some(status => newStatus.toLowerCase().includes(status.toLowerCase()));
    const isChangingFromDNC = dncStatuses.some(status => currentStatus.toLowerCase().includes(status.toLowerCase()));

    if (isChangingToDNC || isChangingFromDNC) {
      setPendingStatusChange(newStatus);
      setShowDNCModal(true);
      return;
    }

    if (newStatus === "Sold & Approved" || newStatus === "Underwriting" || newStatus === "Lapsed" || newStatus === "Declined") {
      // Multiple checks for policy existence
      const hasExistingPolicies =
        (hasPolicies === true) ||
        (policyCount > 0) ||
        (policies && Array.isArray(policies) && policies.length > 0);

      if (hasExistingPolicies && policies && policies.length > 0) {
        // Lead has policies - convert and open edit modal for the most recent policy
        const mostRecentPolicy = getMostRecentPolicy(policies);

        if (!mostRecentPolicy) {
          return;
        }

        const convertedPolicy = convertPolicyForEdit(mostRecentPolicy);

        setEditingPolicy(convertedPolicy);
        setPendingStatusChange(newStatus);
        setShowEditPolicyModal(true);
        return;
      }

      // No policies exist, show modal to create one
      setPendingStatusChange(newStatus);
      setShowPolicyModal(true);
      return;
    }

    proceedWithStatusChange(newStatus);
  };

  const proceedWithStatusChange = async (newStatus: string) => {
    if (!onStatusChange || !leadId) {
      return;
    }

    setUpdatingStatus(true);
    try {
      await onStatusChange(leadId, newStatus);
      setPendingStatusChange(null);
      setShowDNCModal(false);
      setShowPolicyModal(false);
      setShowEditPolicyModal(false);
    } catch (error) {
      // Error handling
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDNCConfirm = async () => {
    if (pendingStatusChange) {
      await proceedWithStatusChange(pendingStatusChange);
    }
  };

  const handlePolicySave = async (success: boolean) => {
    if (success && pendingStatusChange) {
      // Trigger policy update callback first
      if (onPolicyUpdate) {
        onPolicyUpdate();
      }
      // Then proceed with status change
      await proceedWithStatusChange(pendingStatusChange);
    } else {
      setPendingStatusChange(null);
    }
  };

  const handleEditPolicySave = async (success: boolean) => {
    if (success) {
      // Policy was updated, trigger refresh if callback provided
      if (onPolicyUpdate) {
        onPolicyUpdate();
      }

      // Now proceed with status change if pending
      if (pendingStatusChange) {
        await proceedWithStatusChange(pendingStatusChange);
      }
    }

    // Clean up state
    setEditingPolicy(null);
    setPendingStatusChange(null);
    setShowEditPolicyModal(false);
  };

  return (
    <div className="space-y-4">
      {/* Household Member Alert */}
      {isHouseholdMember && !isPrimaryHouseholdMember && primaryLeadInfo && (
        <Alert className="border-blue-200 bg-blue-50">
          <Home className="h-4 w-4 text-blue-600" />
          <AlertDescription className="flex items-center gap-2">
            <span className="text-blue-900">
              This lead is a household member of
            </span>
            <button
              onClick={() => router.push(`/leads/${primaryLeadInfo.id}`)}
              className="font-semibold text-blue-600 hover:text-blue-800 transition-colors"
            >
              {primaryLeadInfo.name} →
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <Button
            className="text-gray-600 hover:text-gray-900 shrink-0"
            disabled={isUpdating}
            onClick={onBack}
            size="sm"
            variant="ghost"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Leads
          </Button>
          <div className="h-6 w-px bg-gray-300 shrink-0" />
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 truncate">
              {leadName || "Unnamed Lead"}
            </h1>
            <div className="flex items-center gap-3 mt-1 flex-nowrap min-w-0">
              <ViewerDisabled message="Upgrade to change lead status"><DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="group shrink-0 cursor-pointer border-0 bg-transparent p-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-full">
                    {(() => {
                      const currentStageColor = statuses.find(s => s.value === currentStatus)?.color;
                      const currentBadgeStyle = currentStageColor
                        ? { backgroundColor: currentStageColor, color: '#fff', borderColor: currentStageColor }
                        : undefined;
                      const currentBadgeClass = currentStageColor
                        ? "flex items-center gap-2 whitespace-nowrap"
                        : `${getStatusColor(currentStatus)} flex items-center gap-2 whitespace-nowrap`;
                      return (
                    <Badge className={currentBadgeClass} style={currentBadgeStyle}>
                      <span>{getStatusDisplayName(currentStatus)}</span>
                      {updatingStatus ? (
                        <div className="h-3 w-3 border border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      )}
                    </Badge>
                      );
                    })()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 p-1 max-h-[320px] overflow-y-auto" sideOffset={4}>
                  {loadingStatuses ? (
                    <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                      <div className="h-3 w-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <React.Fragment>
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Change Status
                      </div>
                      <div className="space-y-1">
                        {statuses.map((status) => {
                          const isSelected = currentStatus === status.value;
                          const badgeStyle = status.color
                            ? { backgroundColor: status.color, color: '#fff', borderColor: status.color }
                            : undefined;
                          const badgeClass = status.color
                            ? "text-xs"
                            : `${getStatusColor(status.value)} text-xs`;
                          return (
                            <DropdownMenuItem
                              key={status.value}
                              disabled={updatingStatus}
                              onClick={() => handleStatusChange(status.value)}
                              className={`cursor-pointer rounded-md px-2 py-1.5 ${isSelected ? "bg-accent/50 font-medium" : ""}`}
                            >
                              <div className="flex items-center justify-between w-full">
                                <Badge className={badgeClass} style={badgeStyle}>
                                  {getStatusDisplayName(status.value)}
                                </Badge>
                                {isSelected && <span className="ml-2 text-xs text-muted-foreground">Current</span>}
                              </div>
                            </DropdownMenuItem>
                          );
                        })}
                      </div>
                    </React.Fragment>
                  )}
                </DropdownMenuContent>
              </DropdownMenu></ViewerDisabled>

              {parentLead && (
                <Badge
                  className="bg-purple-100 text-purple-800 border-purple-200 cursor-pointer hover:bg-purple-200 transition-colors"
                  title="Click to view primary policyholder"
                  onClick={() => router.push(`/leads/${parentLead.id}`)}
                >
                  <Users className="h-3 w-3 mr-1" />
                  This is a beneficiary of lead, {parentLead.name}
                </Badge>
              )}
              {(() => {
                const d: any = 'data' in lead ? lead.data : lead;
                const fmt = (v: any) => (v ? new Date(v).toLocaleDateString() : null);
                const created = fmt(d?.createdAt);
                const updated = fmt(d?.updatedAt);
                const showUpdated = !!updated && updated !== created;
                return (
                  <span className="flex flex-col justify-center min-w-0 text-xs leading-tight text-gray-500">
                    <span className="truncate whitespace-nowrap">Created {created || "Unknown"}</span>
                    {showUpdated && <span className="truncate whitespace-nowrap">Updated {updated}</span>}
                  </span>
                );
              })()}
              {leadId && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label="Copy lead ID"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(String(leadId));
                            setLeadIdCopied(true);
                            window.setTimeout(() => setLeadIdCopied(false), 1500);
                          } catch { toast.error('Could not copy'); }
                        }}
                        className={`inline-flex h-6 shrink-0 items-center gap-1 rounded-full border px-2 font-mono text-[11px] leading-none transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${leadIdCopied ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300 hover:bg-gray-100 hover:text-gray-800'}`}
                      >
                        {leadIdCopied ? (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                        ) : (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V6a2 2 0 0 1 2-2h9" /></svg>
                        )}
                        <span>{leadIdCopied ? 'Copied' : `#${String(leadId).slice(0, 8)}`}</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="font-mono text-xs">{leadId}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {noSaleReason && currentStatus === "No sale" && (
              <p className="text-xs text-gray-900 mt-1">
                Reason: {noSaleReason}
              </p>
            )}
          </div>
        </div>

        <Dialog open={lpConnectOpen} onOpenChange={setLpConnectOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-700 text-white"><Star className="h-4 w-4" /></span>
                Connect Licensedproducer first
              </DialogTitle>
              <DialogDescription>
                Review invites go out from your public <span className="font-semibold text-emerald-700">Licensedproducer</span> profile. Connect it once in Settings — it takes a minute and uses your InsuraCentral login — then come back here to send {riFirst} a one-tap review link.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border bg-muted/40 px-3 py-2.5 text-sm space-y-1.5">
              <p className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-700" /> Verified public listing, auto-approved for InsuraCentral agents</p>
              <p className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-700" /> Quote requests land in this CRM, tagged</p>
              <p className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-700" /> One-tap review invites by email and text</p>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setLpConnectOpen(false)}>Not now</Button>
              <Button
                onClick={() => { setLpConnectOpen(false); router.push('/settings?tab=account&accountTab=profile&connect=licensedproducer'); }}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                Go to Settings
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={reviewInviteOpen} onOpenChange={setReviewInviteOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-700 text-white"><Check className="h-4 w-4" /></span>
                Request a review from {riFirst}
              </DialogTitle>
              <DialogDescription>
                We&rsquo;ll send a one-tap review link for your public <span className="font-semibold text-emerald-700">Licensedproducer</span> profile — no account or signup needed on their end. The link is single-use and goes straight to them.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-1">
              {(() => {
                const canEmail = !!riEmail;
                const canSms = !!riPhone && lpSmsReady;
                const opts: { value: 'email' | 'sms' | 'both'; label: string; icon: React.ReactNode }[] = [];
                if (canEmail) opts.push({ value: 'email', label: 'Email', icon: <Mail className="h-4 w-4 text-emerald-700" /> });
                if (canSms) opts.push({ value: 'sms', label: 'Text message', icon: <MessageSquare className="h-4 w-4 text-emerald-700" /> });
                if (canEmail && canSms) opts.push({ value: 'both', label: 'Email + Text message', icon: <Star className="h-4 w-4 text-emerald-700" /> });
                const current = opts.some((o) => o.value === riChannel) ? riChannel : opts[0]?.value;
                const showEmail = current === 'email' || current === 'both';
                const showSms = current === 'sms' || current === 'both';
                return (
                  <>
                    {opts.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1.5">Send via</p>
                        <Select value={current} onValueChange={(v) => setRiChannel(v as 'email' | 'sms' | 'both')}>
                          <SelectTrigger className="h-11 w-full">
                            <SelectValue placeholder="Choose how to send" />
                          </SelectTrigger>
                          <SelectContent>
                            {opts.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                <span className="flex items-center gap-2">{o.icon}{o.label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {showEmail && canEmail && (
                      <div className="flex items-center gap-3 rounded-lg border bg-muted/40 px-3 py-2.5 text-sm">
                        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-700"><Mail className="h-4 w-4" /></span>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium leading-none">Email</p>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{riEmail} — sent by Licensedproducer</p>
                        </div>
                      </div>
                    )}
                    {showSms && canSms && (
                      <div className="flex items-center gap-3 rounded-lg border bg-muted/40 px-3 py-2.5 text-sm">
                        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-700"><MessageSquare className="h-4 w-4" /></span>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium leading-none">Text message</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{formatPhoneNumber(riPhone)} — lands in this lead&rsquo;s SMS thread</p>
                        </div>
                      </div>
                    )}
                    {showSms && canSms && riNumbers.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1.5">Send from</p>
                        <Select value={riFrom} onValueChange={setRiFrom}>
                          <SelectTrigger className="h-10 w-full">
                            <SelectValue placeholder="Select a number" />
                          </SelectTrigger>
                          <SelectContent>
                            {riNumbers.map((p: any) => {
                              const linked = p?.campaignLinkStatus === 'linked';
                              const area = String(p?.phoneNumber || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '').slice(0, 3);
                              return (
                                <SelectItem key={p?.id || p?.phoneNumber} value={p?.phoneNumber}>
                                  <span className="flex items-center gap-2">
                                    <span className="font-medium">{formatPhoneNumber(p?.phoneNumber || '')}</span>
                                    {(p?.state || area) && <span className="text-xs text-muted-foreground">{p?.state || area}</span>}
                                    {linked && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">A2P</span>}
                                  </span>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        {riFromHint && <p className="text-xs text-muted-foreground mt-1">{riFromHint}</p>}
                      </div>
                    )}
                    {riPhone && !lpSmsReady && (
                      <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm">
                        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700"><MessageSquare className="h-4 w-4" /></span>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium leading-none text-amber-900">Text message unavailable</p>
                          <p className="text-xs text-amber-800 mt-1">
                            Texting {formatPhoneNumber(riPhone)} from your number needs a verified A2P registration.
                            {riEmail ? ' The email invite will still go out.' : ' This lead has no email on file, so nothing can be sent yet.'}
                          </p>
                          <a href="/settings?tab=a2p-verification" className="mt-1.5 inline-block text-xs font-semibold text-amber-900 underline underline-offset-2">Complete A2P verification</a>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setReviewInviteOpen(false)} disabled={sendingReviewInvite}>Cancel</Button>
              <Button
                onClick={sendReviewInviteNow}
                disabled={sendingReviewInvite || !(riEmail || (riPhone && lpSmsReady))}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                <Star className="h-4 w-4 mr-2" />
                {sendingReviewInvite ? 'Sending…' : 'Send Invite'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <DNCConfirmationModal
          isOpen={showDNCModal}
          leadName={leadName}
          onConfirm={handleDNCConfirm}
          onOpenChange={setShowDNCModal}
          onRecover={async () => {
            setPendingStatusChange(null);
            setShowDNCModal(false);
          }}
          isRecoveringFromDNC={currentStatus.toLowerCase().includes('do not call') || currentStatus.toLowerCase().includes('dnc')}
          newStatus={pendingStatusChange || undefined}
        />

        <CreateLeadPolicy
          isOpen={showPolicyModal}
          leadId={leadId || ""}
          leadName={leadName}
          onOpenChange={setShowPolicyModal}
          onSave={handlePolicySave}
          onStatusUpdate={onStatusChange}
          newStatus={pendingStatusChange || undefined}
        />

        <UpdatePolicyDialog
          isOpen={showEditPolicyModal}
          leadId={leadId || ""}
          policy={editingPolicy}
          onOpenChange={(open) => {
            if (!open) {
              setShowEditPolicyModal(false);
              setEditingPolicy(null);
              setPendingStatusChange(null);
            }
          }}
          onSave={handleEditPolicySave}
          onStatusChange={onStatusChange}
          newStatus={pendingStatusChange || undefined}
        />

        {/* Edit button, Navigation, and Auto-save status */}
        <div className="flex items-center gap-3 shrink-0">
          {/* Edit button or Auto-save status indicator */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            {viewOnly ? (
              <span className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                <Eye className="h-3.5 w-3.5" /> View only
              </span>
            ) : !isEditing ? (
              <React.Fragment>
                {/* Review invites: SOLD clients only. Always visible so agents
                    discover it; unlinked agents get a "connect first" dialog
                    that routes them to the settings card. */}
                {(currentStatus || '').toLowerCase().includes('sold') && (
                <TooltipProvider>
                <Tooltip>
                <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={sendingReviewInvite}
                  onClick={() => {
                    if (!lpLinked) { setLpConnectOpen(true); return; }
                    const d: any = 'data' in lead ? lead.data : lead;
                    if (!d?.email && !d?.phone && !d?.phoneNumber) {
                      toast.error('This lead has no email or phone number on file.');
                      return;
                    }
                    const hasEmail = !!d?.email;
                    const hasSms = !!(d?.phone || d?.phoneNumber) && lpSmsReady;
                    setRiChannel(hasEmail && hasSms ? 'both' : hasSms ? 'sms' : 'email');
                    setReviewInviteOpen(true);
                  }}
                >
                  <Star className="h-4 w-4 mr-2 text-amber-500" />
                  {sendingReviewInvite ? 'Sending…' : 'Request Review'}
                </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="font-semibold mb-0.5">Licensedproducer review invite</p>
                  <p className="text-xs">{lpLinked
                    ? 'Emails (and texts, from your number) this client a one-tap link to review you on your public Licensedproducer profile — no account needed on their end. The link is single-use and goes straight to them.'
                    : 'Connect your Licensedproducer profile in Settings to send this client a one-tap review link.'}</p>
                </TooltipContent>
                </Tooltip>
                </TooltipProvider>
                )}
                <ViewerDisabled message="Upgrade to edit leads">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={onEdit}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Lead
                  </Button>
                </ViewerDisabled>
              </React.Fragment>
            ) : isAutoSaving ? (
              <React.Fragment>
                <Cloud className="h-4 w-4 animate-pulse text-blue-500" />
                <span className="text-blue-500">Saving...</span>
              </React.Fragment>
            ) : lastSavedAt ? (
              <React.Fragment>
                <Check className="h-4 w-4 text-green-500" />
                <span className="text-green-600">Auto-saved</span>
              </React.Fragment>
            ) : null}
          </div>

          {/* Lead Navigation - always show arrows */}
          <div className="flex items-center gap-1 border-l border-gray-200 pl-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onPrevLead}
              disabled={!hasPrevLead || isUpdating || isLoadingNavigation}
              className="h-8 w-8 p-0"
              title="Previous lead"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onNextLead}
              disabled={!hasNextLead || isUpdating || isLoadingNavigation}
              className="h-8 w-8 p-0"
              title="Next lead"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};